import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import {
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../components/ui';
import { ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';

/**
 * Form creazione/modifica utente (admin)
 */
function UserFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [operators, setOperators] = useState([]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'client',
    firstName: '',
    lastName: '',
    phone: '',
    guestName: '',
    guestRoom: '',
    assignedOperator: 'none',
    isActive: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadOperators();
    if (isEditing) {
      loadUser();
    }
  }, [id]);

  const loadOperators = async () => {
    try {
      const response = await userService.getOperators();
      setOperators(response.data?.operators || []);
    } catch (error) {
      console.error('Errore caricamento operatori:', error);
    }
  };

  const loadUser = async () => {
    setIsLoading(true);
    try {
      const response = await userService.getById(id);
      const user = response.data?.user;
      if (user) {
        setFormData({
          email: user.email || '',
          password: '', // Non precompilare la password
          role: user.role || 'client',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phone: user.phone || '',
          guestName: user.guestName || '',
          guestRoom: user.guestRoom || '',
          assignedOperator: user.assignedOperator?._id || 'none',
          isActive: user.isActive !== false,
        });
      }
    } catch (error) {
      toast.error('Errore', 'Impossibile caricare i dati dell\'utente');
      navigate('/users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Rimuovi errore del campo modificato
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email obbligatoria';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato email non valido';
    }

    if (!isEditing && !formData.password) {
      newErrors.password = 'Password obbligatoria';
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = 'La password deve avere almeno 8 caratteri';
    }

    if (!formData.firstName) {
      newErrors.firstName = 'Nome obbligatorio';
    }

    if (!formData.lastName) {
      newErrors.lastName = 'Cognome obbligatorio';
    }

    if (formData.role === 'client' && !formData.guestName) {
      newErrors.guestName = 'Nome ospite obbligatorio per i clienti';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    // DEBUG: Log per verificare email prima dell'invio
    console.log('=== DEBUG FORM SUBMIT ===');
    console.log('formData.email:', formData.email);
    console.log('Email charCodes:', [...formData.email].map(c => `${c}:${c.charCodeAt(0)}`).join(' '));

    setIsSaving(true);
    try {
      const data = { ...formData };

      // Rimuovi password se vuota (in modifica)
      if (!data.password) {
        delete data.password;
      }

      // Rimuovi campi cliente se non è un cliente
      if (data.role !== 'client') {
        delete data.guestName;
        delete data.guestRoom;
        delete data.assignedOperator;
      }

      // Rimuovi assignedOperator se vuoto o "none"
      if (!data.assignedOperator || data.assignedOperator === 'none') {
        delete data.assignedOperator;
      }

      if (isEditing) {
        await userService.update(id, data);
        toast.success('Utente aggiornato', 'Le modifiche sono state salvate');
      } else {
        await userService.create(data);
        toast.success('Utente creato', 'L\'utente è stato creato con successo');
      }

      navigate('/users');
    } catch (error) {
      const message = error.response?.data?.message || 'Errore durante il salvataggio';
      toast.error('Errore', message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/users')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Torna alla lista
        </Button>
        <h1 className="text-2xl font-bold text-foreground">
          {isEditing ? 'Modifica Utente' : 'Nuovo Utente'}
        </h1>
        <p className="text-muted-foreground">
          {isEditing
            ? 'Modifica i dati dell\'utente'
            : 'Compila il form per creare un nuovo utente'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Informazioni Account</CardTitle>
            <CardDescription>Dati di accesso dell'utente</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="nome@esempio.it"
                className={errors.email ? 'border-destructive' : ''}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">
                Password {isEditing ? '(lascia vuoto per non modificare)' : '*'}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="••••••••"
                  className={errors.password ? 'border-destructive pr-10' : 'pr-10'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
            </div>

            {/* Ruolo */}
            <div className="space-y-2">
              <Label htmlFor="role">Ruolo *</Label>
              <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona ruolo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Amministratore</SelectItem>
                  <SelectItem value="operator">Operatore</SelectItem>
                  <SelectItem value="client">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Dati Personali</CardTitle>
            <CardDescription>Informazioni anagrafiche</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="firstName">Nome *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="Mario"
                  className={errors.firstName ? 'border-destructive' : ''}
                />
                {errors.firstName && (
                  <p className="text-sm text-destructive">{errors.firstName}</p>
                )}
              </div>

              {/* Cognome */}
              <div className="space-y-2">
                <Label htmlFor="lastName">Cognome *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Rossi"
                  className={errors.lastName ? 'border-destructive' : ''}
                />
                {errors.lastName && (
                  <p className="text-sm text-destructive">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Telefono */}
            <div className="space-y-2">
              <Label htmlFor="phone">Telefono</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+39 333 1234567"
              />
            </div>
          </CardContent>
        </Card>

        {/* Sezione cliente */}
        {formData.role === 'client' && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Informazioni Ospite</CardTitle>
              <CardDescription>Dati relativi all'ospite in struttura</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Nome ospite */}
                <div className="space-y-2">
                  <Label htmlFor="guestName">Nome Ospite *</Label>
                  <Input
                    id="guestName"
                    value={formData.guestName}
                    onChange={(e) => handleChange('guestName', e.target.value)}
                    placeholder="Nome dell'ospite in struttura"
                    className={errors.guestName ? 'border-destructive' : ''}
                  />
                  {errors.guestName && (
                    <p className="text-sm text-destructive">{errors.guestName}</p>
                  )}
                </div>

                {/* Stanza */}
                <div className="space-y-2">
                  <Label htmlFor="guestRoom">Stanza/Reparto</Label>
                  <Input
                    id="guestRoom"
                    value={formData.guestRoom}
                    onChange={(e) => handleChange('guestRoom', e.target.value)}
                    placeholder="Es: 101, Piano 2"
                  />
                </div>
              </div>

              {/* Operatore assegnato */}
              <div className="space-y-2">
                <Label htmlFor="assignedOperator">Operatore Assegnato</Label>
                <Select
                  value={formData.assignedOperator}
                  onValueChange={(value) => handleChange('assignedOperator', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona operatore (opzionale)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun operatore</SelectItem>
                    {operators.map((op) => (
                      <SelectItem key={op._id} value={op._id}>
                        {op.firstName} {op.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stato account (solo in modifica) */}
        {isEditing && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Stato Account</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Account attivo</p>
                  <p className="text-sm text-muted-foreground">
                    Gli account disattivati non possono accedere
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer con azioni */}
        <div className="mt-6 flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/users')}>
            Annulla
          </Button>
          <Button type="submit" isLoading={isSaving}>
            {isEditing ? 'Salva modifiche' : 'Crea utente'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default UserFormPage;
